<template>
    <card title="AR Statement Batch">
        <div v-if="arConfigLoading" class="text-center p-3 d-flex align-items-center justify-content-center"
            style="gap: 6px;">
            <b-spinner small /> Loading...
        </div>
        <div v-else-if="arConfigUnavailable" class="text-center p-3 text-muted">
            Accounts Receivable settings could not be loaded. Refresh to try again.
        </div>
        <div v-else-if="arDisabled" class="text-center p-3 text-muted">
            Accounts Receivable is not enabled for this pharmacy.
        </div>
        <template v-else>
            <p class="text-muted">
                The monthly batch generates a statement for every account due on the closing day, then
                renders, archives, and delivers each one through that account's configured channels.
                It runs automatically on the configured Statement Closing Day &mdash; use this page to
                trigger a run manually.
            </p>

            <ARStatementPeriodFields :period-start.sync="periodStart" :period-end.sync="periodEnd"
                :due-by-date.sync="dueByDate">
                <b-button variant="primary" :disabled="working || !hasValidPeriod" @click="confirmRunBatch">
                    <b-spinner v-if="working" small class="mr-1" />
                    Run Monthly Batch
                </b-button>
            </ARStatementPeriodFields>

            <!-- A zero count is a real outcome, not a success: green "Queued 0" reads as a run
                 that worked but did nothing explicable. The endpoint returns a bare count, so the
                 message names both things that produce a zero rather than guessing which. -->
            <b-alert :show="lastRunCount !== null" :variant="lastRunCount ? 'success' : 'info'" class="mt-2">
                <template v-if="lastRunCount">
                    Queued {{ lastRunCount }} statement(s). Generation and delivery continue in the background;
                    check <strong>AR &rsaquo; Reports &rsaquo; Statement Run History</strong> for the result.
                </template>
                <template v-else>
                    Nothing to queue. Every eligible account either already has a statement for this
                    closing day or is not Active or On Hold. To re-send one that was generated but never
                    delivered, use <strong>Actions &rsaquo; Re-send Statement</strong> on
                    <strong>AR &rsaquo; Statements</strong>.
                </template>
            </b-alert>
        </template>
        </card>
</template>

<script lang="ts">
import { Component, Mixins } from "vue-property-decorator";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import AREnabledMixin from "@/mixins/AREnabledMixin";
import {
    arDefaultDueByDate,
    arDefaultPeriodEnd,
    arDefaultPeriodStart,
    arIsValidStatementPeriod,
} from "@/models/AR";
import ARStatementPeriodFields from "@/components/Patient/ARStatementPeriodFields.vue";

@Component({
    name: "ARStatementBatchPage",
    components: {
        ARStatementPeriodFields,
    },
})
export default class ARStatementBatchPage extends Mixins(AREnabledMixin) {
    private working = false;
    // Null until a run completes, so the result banner stays hidden on first load rather than
    // reading "Queued 0 statement(s)" before anything has been run.
    private lastRunCount: number | null = null;

    private periodStart: string = arDefaultPeriodStart();
    private periodEnd: string = arDefaultPeriodEnd();
    private dueByDate: string = arDefaultDueByDate();

    get hasValidPeriod(): boolean {
        return arIsValidStatementPeriod(this.periodStart, this.periodEnd);
    }

    async mounted(): Promise<void> {
        await this.loadArEnabled();
    }

    // The batch touches every account due and delivers to patients, so it asks first — an
    // accidental click on the wrong period emails the whole pharmacy's responsible parties.
    async confirmRunBatch(): Promise<void> {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Generate and deliver statements for every account due, covering ` +
            `${this.formatDate(this.periodStart)} through ${this.formatDate(this.periodEnd)}?`,
            { title: "Run Monthly Batch", okTitle: "Run Batch", okVariant: "primary", cancelTitle: "Cancel" }
        );
        if (confirmed) await this.runBatch();
    }

    private async runBatch(): Promise<void> {
        try {
            this.working = true;
            const response = await axios.post<number>("/AR/statements/run-batch", {
                periodStart: this.periodStart,
                periodEnd: this.periodEnd,
                dueByDate: this.dueByDate,
            });
            // The result banner below carries the count plus the follow-up pointer, so a toast
            // saying the same thing would be the second report of one event.
            this.lastRunCount = response.data ?? 0;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.working = false;
        }
    }

    formatDate(date?: string): string {
        return date ? moment(date).format("L") : "";
    }
}
</script>

<style scoped></style>
